import _createClient from "openapi-fetch";

import type { paths } from "./monolith-type-overrides";
import type { components } from "./monolith-v4-api";

export type ZapierApiClient = ReturnType<typeof _createClient<paths>>;

/** This error will be thrown by the `ZapierClient` if a request fails */
export class ZapierFacadeError extends Error {
  override name = "ZapierFacadeError" as const;

  /** The endpoint that was requested */
  endpoint: string;

  /** The operation that was requested */
  operation: "GET" | "POST" | "PUT" | "DELETE";

  /** Parameters passed to the request */
  requestParams: any;

  /** Response from the actual HTTP request */
  response: Response;

  /** Errors from the request, if any */
  errors: any[];

  constructor(
    endpoint: string,
    operation: "GET" | "POST" | "PUT" | "DELETE",
    requestParams: any,
    response: Response,
    error: any,
  ) {
    super(
      `Monolith API Error: [${operation} ${endpoint}] ${response.status} ${response.statusText}`,
    );
    this.endpoint = endpoint;
    this.operation = operation;
    this.requestParams = requestParams;
    this.response = response;
    this.errors = this.normalizeErrors(error);
  }

  private normalizeErrors(error: any): any {
    const errors = error?.errors;
    if (!Array.isArray(errors)) return [JSON.stringify(error, null, 2)];

    const errorPatterns = [
      // Replace verbose error messages
      {
        match: "Error while invoking: Error during execution:",
        replace: "Error during execution:",
      },
      // Handle restricted action errors
      // NOTE: There's also a `halt_reason` field for the results, but it doesn't come back from the API
      {
        match:
          "Error from halted execution: Unable to execute restricted action.",
        replace:
          "Access restricted error: This action is restricted by your admin.",
      },
      // Handle authorization errors
      {
        match: /Error during execution: (Authorization .+)/,
        replace: "Authorization error: $1",
      },
      // Handle authentication reconnection errors
      {
        match:
          /Error from halted execution: (Something went wrong with authentication.+)/,
        replace: "Authentication error: $1",
      },
      // Handle task usage limit errors
      {
        match: /(insufficient tasks on account)/i,
        replace:
          "$1. To add more tasks to your account, visit: https://zapier.com/pricing",
      },
    ];

    // Apply transformations to each error
    return errors.map((err) =>
      typeof err === "string"
        ? errorPatterns.reduce(
            (message, { match, replace }) => message.replace(match, replace),
            err,
          )
        : error,
    );
  }
}

/**
 * Returns context for a ZapierFacadeError that can be used in i.e. Sentry.
 */
export const getFacadeErrorContext = (error: ZapierFacadeError) => ({
  endpoint: error.endpoint,
  operation: error.operation,
  requestParams: JSON.stringify(error.requestParams, null, 2),
  responseStatus: error.response?.status,
  errors: JSON.stringify(error.errors, null, 2),
});

export type ActionType = components["schemas"]["Action"]["type"];

export type ImplementationSearchResult =
  components["schemas"]["ImplementationMetaSearchResults"] & {
    versionless_selected_api: string;
  };

export interface AppSearchResponse {
  results: ImplementationSearchResult[];
}

export interface AppDetails {
  app_needs_auth: boolean;
  implementation: components["schemas"]["Implementation"];
}

export type ActionSchema = Omit<
  components["schemas"]["Action"],
  // Omitting needs here since we want to always fetch them from a separate endpoint
  // Why? The needs that comes back on the action info from the API is only the static needs which will exclude a lot of them.
  // I omit them from the type so that we don't run into bugs with "Why aren't all of the needs available?!"
  // Use 'getActionInfoAndNeeds' to get the full action needs
  | "needs"
  // optional in type from monolith, but it always comes back
  | "selected_api"
> & {
  selected_api: string;

  /** Selected API, minus the version */
  versionless_selected_api: string;

  app_name: string;

  /** This is computed and is used when rendering the action in the UI */
  display_name: string;

  /** Determines whether a user can use that action according to restrictions settings */
  permissions?: {
    can_use?: boolean;
  };

  /**
   * If this is true, then an `authentication_id` must be provided when running this action.
   *
   * Authentications available to the user can be listed using the List Authentications For App endpoint.
   */
  app_needs_auth: boolean;

  meta?: {
    hook?: {
      /** If this is present, then this action is a webhook action and has special UI. */
      url: string;
    };
  };
};

export type AuthenticationSchema = Omit<
  components["schemas"]["Authentication"],
  "id" | "is_stale" | "is_shared"
> & {
  // monolith types have this as optional but it's always present
  id: number;

  // monolith types have this as a string but it's always a boolean
  is_stale: boolean;

  // monolith types have this as a string but it's always a boolean
  is_shared: boolean;

  // Whether this authentication is managed by account admins (no individual owner).
  // Sourced from the authentications service; not on the monolith v4 schema, so
  // it may be absent at runtime when fetched via /api/v4/authentications/.
  // Treat undefined as "not managed" at the call site (e.g. `is_managed === true`).
  is_managed?: boolean;
};

// The type we use in the facade
export type NeedChoicesSchema = components["schemas"]["NeedChoices"];
export type NeedSchema = Omit<components["schemas"]["Need"], "type"> & {
  // some of these types are not present in the monolith types, but still come back from the API
  type:
    | "int"
    | "integer"
    | "float"
    | "decimal"
    | "unicode"
    | "text"
    | "str"
    | "string"
    | "datetime"
    | "boolean"
    | "bool"
    | "file"
    | "copy"
    | "password"
    | "dict"
    | "code"
    | "filter"
    | "confirmation"
    | "json"
    | "unicode";

  choices?: NeedChoicesSchema[];
};

export interface ActionInfoSchema {
  action: ActionSchema;
  needs: NeedSchema[];
}

// TODO monolith types are incorrect for `/api/v4/authentications/{id}/test/` but we should check later if they're updated
export interface TestAuthResponse {
  success: boolean;
  errors: Record<string, unknown>[] | null;
  results: Record<string, unknown> | null;
}

/**
 * A connection identifier: either its legacy numeric authentication id or its
 * public UUID. Zapier accepts both and resolves the UUID itself, so a caller
 * passes whichever form it holds without looking the other one up.
 */
export type ConnectionId = number | string;

// These restate `authentication_id` rather than taking it from the generated
// schema, whose snapshot still types it as an integer.
type WithConnectionId<T> = Omit<T, "authentication_id"> & {
  authentication_id?: ConnectionId;
};

export type NeedChoicesRequest = WithConnectionId<
  components["schemas"]["NeedChoicesRequest"]
>;
export type InvokeRequest = WithConnectionId<
  components["schemas"]["InvokeRequest"]
>;
export type NeedsRequest = WithConnectionId<
  components["requestBodies"]["NeedsRequest"]["content"]["application/json"]
>;
export type GivesRequest = WithConnectionId<
  components["requestBodies"]["GivesRequest"]["content"]["application/json"]
>;

export type InvokeResponse = components["schemas"]["InvokeResponse"];
export type InvokeAsyncResponse = components["schemas"]["InvokeAsyncResponse"];
